import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { UseMutationResult } from "@tanstack/react-query";

import { API_ROUTES } from "../../api/ApiRoutes";
import type {
  CreateCampaignTaskRequest,
  CreateCampaignTaskResponse,
  CreatedCampaignTaskRecord,
} from "../../types/gamePass";
import { getPrivateInstance } from "../_privateInstance";
import { unwrapGamePassEntityOrNull } from "./responseMappers";

/**
 * Create the tiered task under a campaign challenge group
 * (`POST /gamepass/v2/admin/tasks`). Campaigns reuse the season task endpoint
 * with `campaign_id` in place of `season_id` (see `ApiRoutes.js`).
 *
 * Tolerant unwrap (FE-1692 pattern): a missing / unrecognised body after a
 * successful POST returns `null` rather than a false creation failure.
 */
export const postCreateCampaignTask = async (
  payload: CreateCampaignTaskRequest,
): Promise<CreatedCampaignTaskRecord | null> => {
  const response = await getPrivateInstance().post<CreateCampaignTaskResponse>(
    API_ROUTES.CREATE_TASK,
    payload,
  );
  return unwrapGamePassEntityOrNull<CreatedCampaignTaskRecord>(
    response.data,
    "task",
  );
};

export const useCreateCampaignTask = (): UseMutationResult<
  CreatedCampaignTaskRecord | null,
  Error,
  CreateCampaignTaskRequest
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["game-pass", "campaign-tasks", "create"],
    mutationFn: postCreateCampaignTask,
    // Mirror `useUpdateCampaignTask`: invalidate the campaign's task + challenge
    // lists (and its detail) so the Monthly Details page re-reads the freshly
    // created task on the next visit rather than a stale cache. Without this the
    // reverse mapper can group a leftover task under the new challenge by name
    // and show the wrong task type.
    onSuccess: (_task, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "tasks", "list"],
      });
      queryClient.invalidateQueries({
        queryKey: ["game-pass", "challenges", "list"],
      });
      if (variables.campaign_id) {
        queryClient.invalidateQueries({
          queryKey: ["game-pass", "campaigns", "detail", variables.campaign_id],
        });
      }
    },
  });
};
